import { datadogRum } from '@datadog/browser-rum';
import { z } from 'zod';

import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import {
  computeResult,
  getAllTrackingEntries,
  getClientPlatform,
  getMetricOperation,
  TrackingKey,
} from '@heidi/runtime/performance';

import { pushDebugLog } from './debug-log';

const LOG_PREFIX = '[PerfAbandonment]';
export const ABANDONED_TRACKINGS_STORAGE_KEY = 'perf.abandoned.v1';
const MAX_RECORDS = 50;
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

/**
 * A flow that was in-flight when the page exited. Durations are frozen at exit
 * time (raw `performance.now()` values are meaningless after a reload), and
 * `exitAt` is wall-clock for staleness pruning. `v` is bumped if the shape
 * changes so stale records from an older build are rejected on read.
 */
const abandonedRecordSchema = z.object({
  v: z.literal(1),
  key: z.nativeEnum(TrackingKey),
  type: z.enum(['interaction', 'load']),
  totalDurationMs: z.number(),
  checkpointDurationsMs: z.record(z.string(), z.number()),
  phaseDurationsMs: z.record(z.string(), z.number()),
  lastCheckpointName: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  exitAt: z.number(),
});

type AbandonedRecord = z.infer<typeof abandonedRecordSchema>;

let installed = false;

function readRecords(): AbandonedRecord[] {
  try {
    const raw = window.localStorage.getItem(ABANDONED_TRACKINGS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Drop any record that fails validation rather than the whole batch.
    return parsed.flatMap((entry) => {
      const result = abandonedRecordSchema.safeParse(entry);
      return result.success ? [result.data] : [];
    });
  } catch {
    return [];
  }
}

function writeRecords(records: AbandonedRecord[]): void {
  try {
    window.localStorage.setItem(
      ABANDONED_TRACKINGS_STORAGE_KEY,
      JSON.stringify(records.slice(-MAX_RECORDS))
    );
  } catch {
    logger.warn(`${LOG_PREFIX} Failed to persist abandoned trackings`);
  }
}

function clearRecords(): void {
  try {
    window.localStorage.removeItem(ABANDONED_TRACKINGS_STORAGE_KEY);
  } catch {
    // Private mode / quota — nothing to clean up.
  }
}

/**
 * Synchronously snapshots in-flight trackings to localStorage on page exit. A
 * local write always completes during teardown (unlike a network beacon), so
 * the data survives a hard refresh / tab close to be replayed on the next load.
 */
export function snapshotActiveTrackings(): void {
  // A flow owned by a native process outlives the page; its own terminal
  // event closes it, so a page exit is not an abandon.
  const entries = getAllTrackingEntries().filter(
    (entry) => entry.metadata?.outlives_page !== true
  );
  if (entries.length === 0) return;

  const endTime = performance.now();
  const exitAt = Date.now();
  const records = entries.map<AbandonedRecord>((entry) => {
    const result = computeResult(entry, endTime);
    return {
      v: 1,
      key: result.key,
      type: result.type,
      totalDurationMs: result.totalDurationMs,
      checkpointDurationsMs: result.checkpointDurationsMs,
      phaseDurationsMs: result.phaseDurationsMs,
      lastCheckpointName: entry.checkpoints.at(-1)?.name ?? null,
      metadata: result.metadata,
      exitAt,
    };
  });

  writeRecords(records);
}

/**
 * Emits persisted abandoned trackings from a prior session to Datadog, then
 * clears them. Runs on a live page so transmission is reliable (unlike emitting
 * during teardown). Call once at app init.
 */
export function replayAbandonedTrackings(): void {
  // TELEMETRY-GATE: the sink is Datadog RUM.
  if (!isSaasTelemetryEnabled || typeof window === 'undefined') return;

  const now = Date.now();
  const records = readRecords().filter((record) => now - record.exitAt <= MAX_AGE_MS);
  // Clear before emitting so a crash mid-replay can't double-report next load.
  clearRecords();

  records.forEach((record) => {
    // The same dimensions `finishTracking` attaches, so an abandoned attempt
    // joins the session-flow metric beside its completed and failed peers.
    const operation = getMetricOperation(record.key);
    const platform = operation ? getClientPlatform() : undefined;
    datadogRum.addAction(record.key, {
      type: record.type,
      totalDurationMs: record.totalDurationMs,
      checkpointDurationsMs: record.checkpointDurationsMs,
      phaseDurationsMs: record.phaseDurationsMs,
      metadata: record.metadata,
      ...(operation ? { operation } : {}),
      ...(platform ? { client_platform: platform } : {}),
      outcome: 'abandoned',
      lastCheckpointName: record.lastCheckpointName,
    });

    pushDebugLog({
      type: 'abandoned',
      key: record.key,
      detail: `total: ${record.totalDurationMs.toFixed(0)}ms`,
      elapsedMs: record.totalDurationMs,
    });
  });
}

/**
 * Registers page-exit listeners that snapshot in-flight trackings to
 * localStorage. Paired with {@link replayAbandonedTrackings} on the next load.
 */
export function installAbandonmentReporter(): void {
  // TELEMETRY-GATE: the sink is Datadog RUM.
  if (!isSaasTelemetryEnabled || installed || typeof window === 'undefined') return;
  installed = true;

  // `pagehide` fires on hard refresh / tab close / navigation and, unlike
  // `beforeunload`, does NOT disqualify the page from bfcache. `persisted`
  // means a bfcache entry — the page may be restored and the tracking finish
  // normally, so don't record it as abandoned.
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted) snapshotActiveTrackings();
  });
}
